import { useEffect } from 'react';
import { onCLS, onLCP, onINP, onTTFB, Metric } from 'web-vitals';

// Global function declarations
declare global {
  function gtag(...args: any[]): void;
  function clarity(command: string, key: string, value?: any): void;
}

// Web Vitals metrics interface
interface WebVitalMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

// Web Vitals thresholds based on Google's recommendations
const VITALS_THRESHOLDS = {
  CLS: { good: 0.1, poor: 0.25 },
  FID: { good: 100, poor: 300 },
  FCP: { good: 1800, poor: 3000 },
  LCP: { good: 2500, poor: 4000 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 }
};

// Rating function
const getRating = (name: string, value: number): 'good' | 'needs-improvement' | 'poor' => {
  const threshold = VITALS_THRESHOLDS[name as keyof typeof VITALS_THRESHOLDS];
  if (!threshold) return 'good';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
};

// Analytics reporting function
const reportWebVital = (metric: WebVitalMetric) => {
  // Send to Google Analytics if available
  if (typeof window !== 'undefined' && 'gtag' in window) {
    gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      custom_map: { metric_rating: metric.rating },
      non_interaction: true
    });
  }

  // Send to Microsoft Clarity if available
  if (typeof window !== 'undefined' && 'clarity' in window) {
    clarity('set', `webvital_${metric.name.toLowerCase()}`, metric.value);
    clarity('set', `webvital_${metric.name.toLowerCase()}_rating`, metric.rating);
  }

  // Console log for development
  if (process.env.NODE_ENV === 'development') {
    console.log(`Web Vital - ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      id: metric.id
    });
  }
};

// Performance observer for monitoring metrics
const createPerformanceObserver = (type: string, callback: (entries: PerformanceEntry[]) => void) => {
  try {
    const observer = new PerformanceObserver((list) => {
      callback(list.getEntries());
    });
    observer.observe({ type, buffered: true });
    return observer;
  } catch (e) {
    // PerformanceObserver not supported
    return null;
  }
};

// Get LCP (Largest Contentful Paint)
const getLCP = (onPerfEntry: (metric: WebVitalMetric) => void) => {
  createPerformanceObserver('largest-contentful-paint', (entries) => {
    const lastEntry = entries[entries.length - 1] as PerformanceEntry & { size?: number };
    if (lastEntry) {
      const value = lastEntry.startTime;
      onPerfEntry({
        name: 'LCP',
        value,
        rating: getRating('LCP', value),
        delta: value,
        id: `lcp-${Date.now()}`
      });
    }
  });
};

// Get FCP (First Contentful Paint)
const getFCP = (onPerfEntry: (metric: WebVitalMetric) => void) => {
  createPerformanceObserver('paint', (entries) => {
    const fcpEntry = entries.find(entry => entry.name === 'first-contentful-paint');
    if (fcpEntry) {
      const value = fcpEntry.startTime;
      onPerfEntry({
        name: 'FCP',
        value,
        rating: getRating('FCP', value),
        delta: value,
        id: `fcp-${Date.now()}`
      });
    }
  });
};

// Get TTFB (Time to First Byte)
const getTTFB = (onPerfEntry: (metric: WebVitalMetric) => void) => {
  createPerformanceObserver('navigation', (entries) => {
    const navEntry = entries[0] as PerformanceNavigationTiming;
    if (navEntry) {
      const value = navEntry.responseStart - navEntry.requestStart;
      onPerfEntry({
        name: 'TTFB',
        value,
        rating: getRating('TTFB', value),
        delta: value,
        id: `ttfb-${Date.now()}`
      });
    }
  });
};

const reportMetric = (metric: Metric) => {
  // In a real application, you would send these metrics to an analytics endpoint.
  // For example, Google Analytics, or a custom backend.
  console.log('[WebVitals]', metric);

  // Example of sending to Google Analytics (if GA is configured):
  // if (window.gtag) {
  //   window.gtag('event', metric.name, {
  //     value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value), // CLS is unitless, others in ms
  //     event_label: metric.id, // id unique to current page load
  //     non_interaction: true, // avoids affecting bounce rate
  //   });
  // }
};

// Main hook
export const useWebVitals = () => {
  useEffect(() => {
    // Report Core Web Vitals
    onCLS(reportMetric);
    onLCP(reportMetric);
    onINP(reportMetric); // Interaction to Next Paint - Modern replacement for FID
    onTTFB(reportMetric); // Time to First Byte

    // Note: INP is the preferred metric for responsiveness.
    // The web-vitals library handles the nuances of when these metrics are reported.
  }, []);
};

export default useWebVitals; 